import React, { useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';
import { useOptionalFieldValidation } from '@/components/validation';
import { useUserConfigDispatch, useUserConfigState } from '@/context/UserConfigContext';
import { blockInvalidNumericInput, handleNumericPaste, formatPhoneNumber } from '@/utils/validation/helpers';
import { toast } from 'sonner';
import {
  MultiSelector,
  MultiSelectorTrigger,
  MultiSelectorInput,
  MultiSelectorContent,
  MultiSelectorList,
  MultiSelectorItem,
} from '@/components/ui/extension/multi-select';
import { COUNTRY_OPTIONS } from '@/constants/countries';

// A memoized component for a single input field.
// It uses useOptionalFieldValidation for inline error display;
// actual validation rules live in the Zod schema (profileSchema).
interface MemoizedInputProps {
  id: string;
  label: string;
  value: string;
  updateFn: (value: string | null) => void;
  placeholder: string;
  [key: string]: unknown;
}

const MemoizedInput = React.memo<MemoizedInputProps>(({
  id,
  label,
  value,
  updateFn,
  placeholder,
  ...props
}) => {
  const { errorElement, onBlur, hasError } = useOptionalFieldValidation(id);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    updateFn(e.target.value || null);
  }, [updateFn]);

  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="flex items-center text-foreground">
        {label} <span className="text-destructive ml-1">*</span>
      </Label>
      <Input
        id={id}
        data-validation-key={id}
        value={value ?? ''}
        onChange={handleChange}
        onBlur={onBlur}
        placeholder={placeholder}
        className={cn(hasError && "border-destructive focus-visible:ring-destructive focus-visible:border-destructive")}
        {...props}
      />
      {errorElement}
    </div>
  );
});
MemoizedInput.displayName = "MemoizedInput";

// Another memoized component, specifically for the phone number.
interface MemoizedPhoneInputProps {
  id: string;
  label: string;
  value: string;
  updateFn: (value: string | null) => void;
  placeholder: string;
  [key: string]: unknown;
}

const MemoizedPhoneInput = React.memo<MemoizedPhoneInputProps>(({
  id,
  label,
  value,
  updateFn,
  placeholder,
  ...props
}) => {
  const { errorElement, onBlur, hasError } = useOptionalFieldValidation(id);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    updateFn(e.target.value || null);
  }, [updateFn]);

  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-foreground">{label}</Label>
      <Input
        id={id}
        data-validation-key={id}
        value={value ?? ''}
        onChange={handleChange}
        onBlur={onBlur}
        placeholder={placeholder}
        onKeyDown={(e) => blockInvalidNumericInput(e, { isPhone: true, allowDecimal: false })}
        onPaste={(e) => handleNumericPaste(e, { isPhone: true, allowDecimal: false })}
        maxLength={14}
        className={cn(hasError && "border-destructive focus-visible:ring-destructive focus-visible:border-destructive")}
        {...props}
      />
      {errorElement}
    </div>
  );
});
MemoizedPhoneInput.displayName = "MemoizedPhoneInput";

interface ContactInformationProps {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  /** When true, shows onboarding-specific helper text for the country picker */
  isOnboarding?: boolean;
}

export const ContactInformation: React.FC<ContactInformationProps> = React.memo(({
  firstName, lastName, email, phone, isOnboarding = false
}) => {
  const { updateProfile, updateCountryCodes } = useUserConfigDispatch();
  const { countryCodes } = useUserConfigState();
  const { user } = useAuth();

  const countryLabels = useCallback((codes: string[]) =>
    codes.map(c => COUNTRY_OPTIONS.find(o => o.value === c)?.label ?? c),
  []);

  const handleCountryChange = useCallback((labels: string[]) => {
    if (labels.length === 0) {
      toast.error('At least one country is required');
      return;
    }
    const codes = labels
      .map(label => COUNTRY_OPTIONS.find(o => o.label === label)?.value)
      .filter((c): c is string => c !== undefined);
    if (codes.length === 0) {
      toast.error('At least one country is required');
      return;
    }
    updateCountryCodes(codes);
  }, [updateCountryCodes]);
  const isGoogleUser =
    user?.app_metadata?.provider === 'google' ||
    user?.identities?.some((id) => id.provider === 'google');

  return (
    <div className="space-y-3 sm:space-y-6">
      <div className="space-y-1 text-center">
        <h3 className="text-lg font-medium text-foreground">Contact Information</h3>
        <p className="text-sm text-muted-foreground">
          Enter your contact details for clients and booking communications
        </p>
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4">
        <MemoizedInput
          id="firstName"
          label="First Name"
          value={firstName}
          updateFn={(v) => updateProfile({ firstName: v ?? '' })}
          placeholder="Your first name"
          autoCapitalize="words"
          className="capitalize"
          maxLength={100}
        />
        <MemoizedInput
          id="lastName"
          label="Last Name"
          value={lastName}
          updateFn={(v) => updateProfile({ lastName: v ?? '' })}
          placeholder="Your last name"
          autoCapitalize="words"
          className="capitalize"
          maxLength={100}
        />
      </div>

      <MemoizedInput
        id="email"
        label="Email Address"
        value={email}
        updateFn={(v) => updateProfile({ email: v ?? '' })}
        placeholder="your.email@example.com"
        disabled={isGoogleUser}
        className={cn(isGoogleUser && 'opacity-50 cursor-not-allowed')}
      />

      <MemoizedPhoneInput
        id="phone"
        label="Phone Number"
        value={phone}
        updateFn={(v) => {
          const formatted = formatPhoneNumber(v ?? '');
          updateProfile({ phone: formatted });
        }}
        placeholder="(555) 123-4567"
      />

      <div className="space-y-2">
        <Label className="text-foreground">Countries you work in</Label>
        <MultiSelector
          values={countryLabels(countryCodes)}
          onValuesChange={handleCountryChange}
          loop={false}
        >
          <MultiSelectorTrigger>
            <MultiSelectorInput placeholder="Select countries..." />
          </MultiSelectorTrigger>
          <MultiSelectorContent>
            <MultiSelectorList>
              {COUNTRY_OPTIONS
                .filter(o => !countryCodes.includes(o.value))
                .map(o => (
                  <MultiSelectorItem key={o.value} value={o.label}>
                    {o.label}
                  </MultiSelectorItem>
                ))}
            </MultiSelectorList>
          </MultiSelectorContent>
        </MultiSelector>
        <p className="text-xs text-muted-foreground">
          {isOnboarding
            ? "We'll use this to show you the right set of public holidays when you set up your rate multipliers."
            : "Determines which public holidays are offered as pickable options. Add more countries if you work across borders."}
        </p>
      </div>

      <p className="text-sm text-muted-foreground mt-6 sm:mt-4">
        <span className="text-destructive">*</span> Required fields
      </p>
    </div>
  );
});
ContactInformation.displayName = 'ContactInformation';
